import React, {useCallback, useEffect, useState} from 'react';
import {useHistory} from 'react-router-dom';
import {loadStripe} from '@stripe/stripe-js';
import {useDispatch, useSelector} from 'react-redux';
import {
  EmbeddedCheckoutProvider,
  EmbeddedCheckout
} from '@stripe/react-stripe-js';
import DashboardContent from 'components/DashboardContent/styles';
import DashboardHeader from 'components/DashboardHeader/styles';
import Headline from 'components/Headline/styles';
import ByocProgressBar from 'components/ByocProgressBar';
import {
  ByocOnboardingLoadingOverlay,
  ByocOnboardingTitle
} from 'routes/App/byocOnboarding/styles';
import {
  PaymentDetailsContainer,
  StripeCheckoutContainer
} from 'routes/App/byocOnboarding/PaymentDetails/styles';
import {ButtonOutline, ButtonPrimary} from 'components/buttons';
import DialogAction from 'components/DialogAction/styles';
import DialogActions from 'components/DialogActions/styles';
import {ErrorSign, ErrorSignWrapper} from 'components/errorSign/errorSign';
import {PROGRESS_BAR_STEPS} from 'helpers/constants/byocOnboarding';
import {
  createStripeCheckoutSession,
  markPaymentDetailsAsFinished,
  updateStripeFlowCompleted
} from 'helpers/services/api/stripe';
import {userData, userInfo} from 'store/global/account/selectors';
import Loading from 'components/shared/Utils/Loading';
import {UPDATE_BYOC_ONBOARDING_STEP} from 'store/byocOnboarding/actions';
import {scrollToFirstError} from 'helpers/functionUtils/scrollToFirstError';
import {EmailMarketingSpinner} from 'routes/App/emailMarketing/components/styles';

const stripePromise = loadStripe(
  process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY ||
    'pk_test_51Sa8P8CrLvKupQVxHOeCZ08iDGHnOgssDaSV4gdgb3Sjuva1uB8puDNuuixMPwQiZaFUWuUEvoFkMSLhoRF3XmC200saUpcvKt'
);

const PaymentDetails = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const user = useSelector(userData);
  const {locationId} = useSelector(userInfo);
  const [checkoutComplete, setCheckoutComplete] = useState(false);
  const [validationError, setValidationError] = useState('');
  const [loading, setLoading] = useState(false);

  const {data, globalLoading} = useSelector(
    (state) => state.byocOnboardingReducer || {}
  );
  const paymentDetails = data?.paymentDetails || data?.PaymentDetails || {};
  // Check paymentMethodConnected instead of stripeFlowCompleted
  const isPaymentMethodNotConnected =
    paymentDetails?.paymentMethodConnected === false ||
    paymentDetails?.paymentMethodConnected === undefined;
  const isPaymentDetailsFinished =
    paymentDetails?.isFinished === true || paymentDetails?.IsFinished === true;
  const orderSourceData =
    data?.orderSourceIntegration || data?.OrderSourceIntegration || {};
  const isOrderSourceFinished =
    orderSourceData?.isFinished === true ||
    orderSourceData?.IsFinished === true;

  const fetchClientSecret = useCallback(async () => {
    const email = user?.email;
    const response = await createStripeCheckoutSession(locationId, email);
    return response?.data?.clientSecret;
  }, [user, locationId]);

  const handleComplete = useCallback(async () => {
    setValidationError('');
    // Save StripeFlowCompleted flag when Stripe flow is completed
    try {
      // await updateStripeFlowCompleted(locationId, true);
      setCheckoutComplete(true);
    } catch (error) {
      console.error('Error updating Stripe flow completed:', error);
    }
  }, [locationId]);

  const options = {
    fetchClientSecret,
    onComplete: handleComplete
  };

  const handleContinue = async () => {
    setLoading(true);
    try {
      dispatch(
        UPDATE_BYOC_ONBOARDING_STEP({
          paymentDetails: {
            paymentMethodConnected: checkoutComplete,
            isFinished: true
          },
          successCallback: () => {
            history.push('/byoc-onboarding/integration-status');
            setLoading(false);
          }
        })
      );
    } catch (error) {
      console.error('Error marking payment details as finished:', error);
      setValidationError('Something went wrong. Please try again.');
      setLoading(false);
    }
  };

  const handleBack = () => {
    history.push('/byoc-onboarding/order-source-integration');
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Scroll to first error when validation error appears
  useEffect(() => {
    if (validationError) {
      scrollToFirstError({
        errorSelectors: [
          '[class*="ErrorSignWrapper"]',
          '[class*="ErrorSign"]',
          '[class*="Error"]'
        ],
        delay: 300
      });
    }
  }, [validationError]);

  return (
    <DashboardContent className='DashboardContent'>
      <DashboardHeader>
        <Headline className='mTop5'>Self Delivery</Headline>
      </DashboardHeader>
      {globalLoading ? (
        <EmailMarketingSpinner>
          <Loading />
        </EmailMarketingSpinner>
      ) : (
        <>
          <ByocProgressBar currentStep={2} steps={PROGRESS_BAR_STEPS} />
          <PaymentDetailsContainer>
            <ByocOnboardingTitle>Payment Details</ByocOnboardingTitle>

            <StripeCheckoutContainer>
              <EmbeddedCheckoutProvider
                stripe={stripePromise}
                options={options}
              >
                <EmbeddedCheckout />
              </EmbeddedCheckoutProvider>
            </StripeCheckoutContainer>

            {validationError && (
              <ErrorSignWrapper className='positionInitial mTop15 centerPosition'>
                <ErrorSign>{validationError}</ErrorSign>
              </ErrorSignWrapper>
            )}

            <DialogActions className='justifyEnd mTop32'>
              {((!isPaymentMethodNotConnected && isPaymentDetailsFinished) ||
                (!isPaymentDetailsFinished &&
                  isOrderSourceFinished &&
                  isPaymentMethodNotConnected)) && (
                <DialogAction className='w30'>
                  <ButtonOutline onClick={handleBack} type='button'>
                    <span>Back</span>
                  </ButtonOutline>
                </DialogAction>
              )}
              <DialogAction className='w30'>
                <ButtonPrimary
                  onClick={handleContinue}
                  type='button'
                  disabled={loading}
                >
                  <span>Continue</span>
                </ButtonPrimary>
              </DialogAction>
            </DialogActions>
            {loading && (
              <ByocOnboardingLoadingOverlay>
                <Loading />
              </ByocOnboardingLoadingOverlay>
            )}
          </PaymentDetailsContainer>
        </>
      )}
    </DashboardContent>
  );
};

export default PaymentDetails;
